<template>
    <MainContainer>
        <template v-slot:sidebar>&nbsp;</template>
        <div class="notentransfer-sv">
            <h2>
                Notentransfer Stimmvertreter
                <b-button
                    v-if="checkRight('write')"
                    :size="size"
                    @click="showIssueModal"
                    variant="danger"
                >
                    Ausgabe
                    <font-awesome-icon icon="arrow-right" />
                </b-button>
                <b-button
                    v-if="checkRight('write')"
                    :size="size"
                    @click="showReturnModal"
                    class="ml-2"
                    variant="success"
                >
                    Rücknahme
                    <font-awesome-icon icon="arrow-left" />
                </b-button>
            </h2>
            <div class="notentransfer-sv-filter">
                <b-row>
                    <b-col cols="4">
                        <b-form-group
                            :label-size="size"
                            label="Offene und/oder geschlossene Notentransaktionen einschließen:"
                            label-for="filterNotentransfers"
                        >
                            <b-select
                                id="filterNotentransfers"
                                :options="filter.notentransfers.options"
                                v-model="filter.notentransfers.value"
                                :size="size"
                            ></b-select>
                        </b-form-group>
                    </b-col>
                    <b-col cols="4">
                        <b-form-group :label-size="size" label="Werk" label-for="filterWerk">
                            <FilteredSelect
                                :size="size"
                                :busy="filter.werke.busy"
                                :options="filter.werke.options"
                                v-model="filter.werke.value"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col cols="4">
                        <b-form-group
                            :label-size="size"
                            label="Stimmverter*in"
                            label-for="filterSV"
                        >
                            <FilteredSelect
                                :size="size"
                                :busy="filter.sv.busy"
                                :options="filter.sv.options"
                                v-model="filter.sv.value"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12">
                        <b-button
                            :size="size"
                            class="ml-auto d-block"
                            @click="clearFilter"
                        >Filter zurücksetzen</b-button>
                    </b-col>
                </b-row>
            </div>
            <hr />
            <Table
                bordered
                striped
                small
                showFilter
                :responsive="true"
                :fields="fields"
                :items="items"
                :isBusy="busy"
                :refresh="refresh"
                sortBy="werk"
                busyText="Lädt Notentransferdaten..."
                emptyText="Keine Notentransferdaten gefunden"
            >
                <template v-slot:detail="{row: row}">
                    <b-button
                        :size="size"
                        variant="secondary"
                        @click="showDetails(row)"
                        class="mv-table-action-btn mr-1 mv-nowrap"
                    >
                        Details
                        <font-awesome-icon
                            :icon="row.item._showDetails ? 'caret-down' : 'caret-right'"
                            class="ml-1"
                        />
                    </b-button>
                </template>

                <template v-slot:row-details="{row: row}">
                    <b-card>
                        <NotentransferDetailSV
                            :sv_id="row.item.sv_id"
                            :noten_id="row.item.noten_id"
                            :werk_id="row.item.werk_id"
                            :notentyp="row.item.notentyp"
                            :totals="{issued: row.item.issued, returned: row.item.returned, returned_total: row.item.returned_total, total: row.item.total}"
                            :refresh="refresh"
                            :disableActions="disableActions"
                            @cancelation="handleCanceledTransaction"
                        />
                    </b-card>
                </template>

                <template v-slot:sv="{row: row}">{{row.value.vorname + " " + row.value.name}}</template>

                <template v-slot:konzert="{row: row}">{{row.value.bezeichnung}}</template>

                <template v-slot:issued="{row: row}">-{{row.value}}</template>

                <template v-slot:returned="{row: row}">{{row.item.returned_total}}</template>

                <template v-slot:total="{row: row}">
                    <template v-if="parseInt(row.item.total) > 0">-{{row.value}}</template>
                    <template v-else>{{row.value}}</template>
                </template>

                <template v-slot:actions="{ row: row }">
                    <b-dropdown text="Aktionen" size="sm" :disabled="disableActions" dropleft>
                        <b-dropdown-item
                            v-if="checkRight('write') && !row.closed"
                            @click="showIssueModal(row)"
                        >
                            <font-awesome-icon icon="arrow-right" class="mr-1 text-danger" />Ausgabe
                        </b-dropdown-item>
                        <b-dropdown-item
                            v-if="checkRight('write') && !row.closed"
                            @click="showReturnModal(row)"
                        >
                            <font-awesome-icon icon="arrow-left" class="mr-1 text-success" />Rücknahme
                        </b-dropdown-item>
                        <b-dropdown-item
                            v-if="checkRight('write') && !row.closed"
                            @click="showErrorEntryModal(row)"
                        >
                            <font-awesome-icon icon="window-close" class="mr-1 text-warning" />Abschreibung
                        </b-dropdown-item>
                        <b-dropdown-item
                            v-if="checkRight('write')"
                            :to="{path: '/notenverwaltung/notentransfer/mitglieder', query: {sv_id: row.sv_id, werk_id: row.werk_id, noten_id: row.noten_id, notentyp: row.notentyp}}"
                        >
                            <font-awesome-icon icon="exchange-alt" class="text-primary mr-1" />Zum Notentransfer
                        </b-dropdown-item>
                    </b-dropdown>
                </template>
            </Table>
            <NotenausgabeSVModal
                :svId="issued.svId"
                :werkId="issued.werkId"
                :notenId="issued.notenId"
                :notentyp="issued.notentyp"
                :busy="selected.busy"
                :validationData="validationData"
                @success="update"
            />
            <NotenruecknahmeSVModal
                :svId="returned.svId"
                :werkId="returned.werkId"
                :notenId="returned.notenId"
                :notentyp="returned.notentyp"
                :busy="selected.busy"
                :validationData="validationData"
                @success="update"
            />
            <NotenfehlbuchungSVModal :record="error_entry" :busy="selected.busy" @success="update" />
        </div>
    </MainContainer>
</template>

<script>
import CommonView from "@/mixins/CommonView/CommonView";
import Size from "@/mixins/Size/Size";
import { ApiFactory } from "@/api/ApiFactory.js";
import MainContainer from "@/components/MainContainer/MainContainer";
import Table from "@/components/Table/Table";
import FilteredSelect from "@/components/FilteredSelect/FilteredSelect";
import NotenausgabeSVModal from "@/components/Notentransfer/NotenausgabeSVModal";
import NotenruecknahmeSVModal from "@/components/Notentransfer/NotenruecknahmeSVModal";
import NotenfehlbuchungSVModal from "@/components/Notentransfer/NotenfehlbuchungSVModal";
import NotentransferDetailSV from "@/components/Notentransfer/NotentransferDetailSV/NotentransferDetailSV";

import "./NotentransferSV.scss";

const NotenverwaltungApi = ApiFactory.get("notenverwaltung");
const NotentransferSVApi = ApiFactory.get("notentransferSV");
const WerkeApi = ApiFactory.get("werke");

export default {
    name: "NotentransferSV",
    mixins: [CommonView, Size],
    components: {
        MainContainer,
        Table,
        FilteredSelect,
        NotenausgabeSVModal,
        NotenruecknahmeSVModal,
        NotenfehlbuchungSVModal,
        NotentransferDetailSV
    },
    data() {
        return {
            fields: [
                {
                    key: "sv",
                    label: "Stimmvertreter",
                    sortable: true
                },
                {
                    key: "detail",
                    label: "Details",
                    class: "text-center"
                },
                {
                    key: "konzert",
                    label: "Konzert",
                    sortable: true
                },
                {
                    key: "werk",
                    label: "Werk",
                    sortable: true
                },
                {
                    key: "komponist",
                    label: "Komponist",
                    sortable: true
                },
                {
                    key: "ausgabe",
                    label: "Ausgabe",
                    sortable: true
                },
                {
                    key: "description",
                    label: "Notenart",
                    sortable: true
                },
                {
                    key: "issued",
                    label: "AUS",
                    class: "text-right",
                    thClass: "text-center",
                    variant: "danger"
                },
                {
                    key: "returned",
                    label: "RÜCK",
                    class: "text-right",
                    thClass: "text-center",
                    variant: "success"
                },
                {
                    key: "total",
                    label: "Summe",
                    thClass: "text-center",
                    class: "text-right font-weight-bold"
                },
                {
                    key: "actions",
                    label: "Funktionen",
                    class: "text-center"
                }
            ],
            api: NotentransferSVApi,
            refresh: false,
            disableActions: false,
            returned: {
                svId: null,
                werkId: null,
                notenId: null,
                notentyp: null
            },
            issued: {
                svId: null,
                werkId: null,
                notenId: null,
                notentyp: null
            },
            error_entry: null,
            filter: {
                notentransfers: {
                    value: "open",
                    options: [
                        { value: "open", text: "nur offene" },
                        { value: "closed", text: "nur geschlossene" },
                        { value: "all", text: "alle" }
                    ]
                },
                werke: {
                    value: null,
                    data: [],
                    options: [],
                    busy: false
                },
                sv: {
                    value: null,
                    data: [],
                    options: [],
                    busy: false
                }
            },
            validationData: null
        };
    },
    computed: {
        filterRendered() {
            return {
                show: this.filter.notentransfers.value,
                filter: {
                    werk_id: this.filter.werke.value,
                    sv_id: this.filter.sv.value
                }
            };
        }
    },
    created() {
        this.fetchWerke();
        this.fetchSV();
    },
    watch: {
        filterRendered: {
            handler() {
                this.fetch();
            },
            deep: true
        }
    },
    methods: {
        async fetch() {
            this.busy = true;
            const response = await this.api.get(this.filterRendered);
            if (response.ok) {
                var items = response.json.collection;
                if (items) {
                    items = items.map(item => this.process(item));
                    this.items = items;
                } else {
                    this.items = [];
                }
                this.registerRights(response);
            }
            this.busy = false;
        },
        process(item, considerDetails = true) {
            if (considerDetails) {
                item._showDetails = false;
            }

            if (item.total > 0) {
                item._cellVariants = { total: "warning" };
            }
            item.closed = parseInt(item.total) === 0;
            return item;
        },
        async fetchWerke() {
            this.filter.werke.busy = true;
            const response = await WerkeApi.get();
            if (response.ok) {
                this.filter.werke.data = response.json.collection;
                this.filter.werke.options = response.json.collection.map(
                    item => {
                        return {
                            value: item.id,
                            text: item.bezeichnung
                        };
                    }
                );
            }
            this.filter.werke.busy = false;
        },
        async fetchSV() {
            this.filter.sv.busy = true;
            const response = await NotenverwaltungApi.getSV();
            if (response.ok) {
                var data = response.json.collection;
                var options = data.map(item => {
                    return {
                        value: item.id,
                        text: item.vorname + " " + item.name
                    };
                });
                this.filter.sv.data = data;
                this.filter.sv.options = options;
            }
            this.filter.sv.busy = false;
        },
        showDetails(row) {
            row.item._showDetails = !row.item._showDetails;
        },
        showIssueModal(row) {
            if (row) {
                this.issued = {
                    svId: row.sv_id,
                    werkId: row.werk_id,
                    notenId: row.noten_id,
                    notentyp: row.notentyp
                };
            }
            this.$root.$emit("bv::show::modal", "notenausgabeSVModal");
        },
        showReturnModal(row) {
            if (row) {
                this.returned = {
                    svId: row.sv_id,
                    werkId: row.werk_id,
                    notenId: row.noten_id,
                    notentyp: row.notentyp
                };
            }
            this.$root.$emit("bv::show::modal", "notenruecknahmeSVModal");
        },
        showErrorEntryModal(row) {
            if (row) {
                this.error_entry = row;
            }
            this.$root.$emit("bv::show::modal", "notenfehlbuchungSVModal");
        },
        clearFilter() {
            Object.keys(this.filter).forEach(key => {
                if (key === "notentransfers") {
                    this.filter[key].value = "open";
                } else {
                    this.filter[key].value = null;
                }
            });
        },
        async update(data) {
            this.selected.busy = true;
            const response = await this.api.post(data);
            if (response.ok) {
                await this.refreshData();
            } else {
                if (response.json.validation) {
                    this.validationData = response;
                }
            }
            this.selected.busy = false;
        },
        async refreshData() {
            const responseFetch = await this.api.get(this.filterRendered);
            if (responseFetch.ok) {
                var items = responseFetch.json.collection;
                if (!items) {
                    items = [];
                }
                var newIdCompositions = [];
                items.map(fetchedItem => {
                    var currentIndex = this.items.findIndex(
                        item =>
                            item.sv_id === fetchedItem.sv_id &&
                            item.werk_id === fetchedItem.werk_id &&
                            item.noten_id === fetchedItem.noten_id &&
                            item.notentyp === fetchedItem.notentyp
                    );
                    if (currentIndex !== -1) {
                        this.items[currentIndex] = Object.assign(
                            this.items[currentIndex],
                            this.process(fetchedItem, false)
                        );
                    } else {
                        this.items.push(this.process(fetchedItem));
                    }
                    newIdCompositions.push({
                        sv_id: fetchedItem.sv_id,
                        werk_id: fetchedItem.werk_id,
                        noten_id: fetchedItem.noten_id,
                        notentyp: fetchedItem.notentyp
                    });
                });
                this.items = this.items
                    .map(item => {
                        var stillIndexed = newIdCompositions.findIndex(
                            newItem =>
                                item.sv_id === newItem.sv_id &&
                                item.werk_id === newItem.werk_id &&
                                item.noten_id === newItem.noten_id &&
                                item.notentyp === newItem.notentyp
                        );
                        if (stillIndexed !== -1) {
                            return item;
                        } else {
                            return null;
                        }
                    })
                    .filter(item => item !== null);
            }
            this.refresh = !this.refresh;
            this.$root.$emit("bv::hide::modal", "notenfehlbuchungSVModal");
            this.$root.$emit("bv::hide::modal", "notenausgabeSVModal");
            this.$root.$emit("bv::hide::modal", "notenruecknahmeSVModal");
            this.makeToast(
                "Erfolgreich gespeichert",
                "Die Änderungen wurden erfolgreich in der Datenbank gespeichert"
            );
        },
        async handleCanceledTransaction(id) {
            this.disableActions = true;
            const response = await NotentransferSVApi.cancelTransaction(id);
            if (response.ok) {
                await this.refreshData();
            }
            this.disableActions = false;
        }
    }
};
</script>
